import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Users, Activity, TrendingUp, Layers, Plus, Radio, Repeat, ArrowLeftRight, IdCard, Lock, Unlock, Snowflake, Trash2, FileDown, KeyRound, Search } from "lucide-react";
import Nav from "@/components/Nav";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import IssueCardDialog from "@/components/IssueCardDialog";
import TransferDialog from "@/components/TransferDialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const THEME_DOT = {
  aurora: "linear-gradient(135deg,#00F0FF,#FF007F)",
  sunset: "linear-gradient(135deg,#FFE600,#FF007F)",
  matrix: "linear-gradient(135deg,#00FF9D,#0047FF)",
  molten: "linear-gradient(135deg,#FF007F,#FFE600)",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  active: "bg-[#00FF9D]/15 text-[#00FF9D] border-0",
  pending: "bg-[#00F0FF]/15 text-[#00F0FF] border-0",
  pending_release: "bg-[#FFE600]/15 text-[#FFE600] border-0",
  released: "bg-[#FF007F]/15 text-[#FF007F] border-0",
  declined: "bg-white/10 text-[#A0A0AB] border-0",
};

const WEIGHT_COLOR = {
  Perfect: "#FFE600",
  Heavy: "#00F0FF",
  Balanced: "#7A5CFF",
  Light: "#52525B",
};

export default function Admin() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [stats, setStats] = useState(null);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [registrar, setRegistrar] = useState({ cards: [], transfers: [] });
  const [requests, setRequests] = useState([]);
  const [modRequests, setModRequests] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issuePrefill, setIssuePrefill] = useState(null);
  const [transferCard, setTransferCard] = useState(null);
  const [publicKey, setPublicKey] = useState(null);
  const [bins, setBins] = useState([]);
  const [newBin, setNewBin] = useState("");
  const [newBinLabel, setNewBinLabel] = useState("");
  const [regQuery, setRegQuery] = useState("");

  const loadAll = async () => {
    const [s, c, u, r, q, m, t, pk, b] = await Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/admin/cards"),
      api.get("/admin/users"),
      api.get("/admin/registrar"),
      api.get("/card-requests"),
      api.get("/admin/moderator-requests"),
      api.get("/admin/topup-requests"),
      api.get("/network/public-key"),
      api.get("/admin/bins"),
    ]);
    if (s.status === "fulfilled") setStats(s.value.data);
    if (c.status === "fulfilled") setCards(c.value.data);
    if (u.status === "fulfilled") setUsers(u.value.data);
    if (r.status === "fulfilled") setRegistrar(r.value.data);
    if (q.status === "fulfilled") setRequests(q.value.data);
    if (m.status === "fulfilled") setModRequests(m.value.data);
    if (t.status === "fulfilled") setTopupRequests(t.value.data);
    if (pk.status === "fulfilled") setPublicKey(pk.value.data);
    if (b.status === "fulfilled") setBins(b.value.data);
  };

  const createBin = async () => {
    if (!newBin.trim()) return toast.error("Enter a BIN (4–12 digits)");
    try {
      await api.post("/admin/bins", { bin: newBin.trim(), label: newBinLabel.trim() });
      toast.success(`BIN ${newBin.trim()} registered`);
      setNewBin(""); setNewBinLabel("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register BIN");
    }
  };

  const toggleBin = async (b) => {
    try {
      await api.post(`/admin/bins/${b.id}/toggle`);
      toast.success(b.active ? "BIN deactivated" : "BIN activated");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const freezeCard = async (c) => {
    try {
      await api.post(`/cards/${c.id}/freeze`);
      toast.success(c.frozen ? "Card unfrozen" : "Card frozen (back-side lock)");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const removeCard = async (c) => {
    if (!window.confirm(`Remove card ${c.nickname || ""} from the network? This releases it.`)) return;
    try {
      await api.post(`/admin/cards/${c.id}/remove`);
      toast.success("Card removed / released");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const downloadReport = () => {
    const token = localStorage.getItem("session_token") || "";
    window.open(`${BACKEND_URL}/api/admin/export/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const approveTopup = async (req) => {
    try {
      await api.post(`/admin/topup-requests/${req.id}/approve`);
      toast.success(`Approved $${Number(req.amount).toFixed(2)} for ${req.nickname}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const declineTopup = async (req) => {
    try {
      await api.post(`/admin/topup-requests/${req.id}/decline`);
      toast.success("Top-up declined");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const toggleLock = async (c) => {
    try {
      await api.post(`/admin/cards/${c.id}/${c.locked ? "unlock" : "lock"}`);
      toast.success(c.locked ? "Card unlocked" : "Card locked & reserved");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const approveMod = async (req) => {
    try {
      await api.post(`/admin/moderator-requests/${req.id}/approve`);
      toast.success(`${req.email} is now a moderator`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const declineMod = async (req) => {
    try {
      await api.post(`/admin/moderator-requests/${req.id}/decline`);
      toast.success("Request declined");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const toggleModerator = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/moderator`, { enable: u.role !== "moderator" });
      toast.success(u.role !== "moderator" ? "Moderator added" : "Moderator removed");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const openIssueForRequest = (req) => {
    setIssuePrefill({ email: req.user_email, nickname: req.nickname || "", deposit: req.amount || 0 });
    setIssueOpen(true);
  };

  const declineRequest = async (req) => {
    try {
      await api.post(`/card-requests/${req.id}/decline`);
      toast.success("Request declined");
      loadAll();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const kpis = [
    { icon: CreditCard, label: "Network Cards", value: stats?.total_cards ?? "—", color: "#00F0FF" },
    { icon: Radio, label: "Cards Online", value: stats?.cards_online ?? "—", color: "#00FF9D" },
    { icon: Repeat, label: "Network Uses", value: stats?.total_uses ?? "—", color: "#FFE600" },
    { icon: Users, label: "Users", value: stats?.total_users ?? "—", color: "#FF007F" },
    { icon: TrendingUp, label: "Payment Volume", value: stats ? `$${stats.total_volume.toLocaleString()}` : "—", color: "#00C2FF" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative">
      <div className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#00F0FF] opacity-[0.06] blur-[140px]" />
      <Nav active="admin" />

      <main className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="font-mono-sig text-[11px] tracking-[0.3em] uppercase text-[#52525B] mb-1">Network Operations</p>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">Admin Console</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-download-report-button"
              onClick={downloadReport}
              variant="outline"
              className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10 font-semibold h-11"
            >
              <FileDown className="w-4 h-4 mr-2" /> Download report (PDF)
            </Button>
            <Button
              data-testid="admin-issue-card-button"
              onClick={() => setIssueOpen(true)}
              className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-11"
            >
              <Plus className="w-4 h-4 mr-2" /> Issue Card
            </Button>
          </div>
        </div>

        {publicKey && (
          <div
            data-testid="network-public-key"
            className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3"
          >
            <KeyRound className="w-4 h-4 text-[#00FF9D] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#52525B] mb-1">
                Network validation key · {publicKey.algorithm} · public (safe to share)
              </p>
              <p className="font-mono-sig text-xs text-[#00F0FF] break-all">{publicKey.public_key}</p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {kpis.map((k, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
            >
              <k.icon className="w-5 h-5 mb-3" style={{ color: k.color }} />
              <p data-testid={`kpi-${i}`} className="font-display font-black text-2xl tracking-tighter">{k.value}</p>
              <p className="font-mono-sig text-[9px] tracking-[0.25em] uppercase text-[#52525B] mt-1">{k.label}</p>
            </motion.div>
          ))}
        </div>

        <Tabs defaultValue="cards" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 rounded-full p-1">
            <TabsTrigger value="cards" data-testid="tab-cards" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Issued Cards
            </TabsTrigger>
            <TabsTrigger value="registrar" data-testid="tab-registrar" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Registrar
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Requests{requests.length > 0 ? ` (${requests.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Users
            </TabsTrigger>
            <TabsTrigger value="mods" data-testid="tab-mods" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Mod Requests{modRequests.length > 0 ? ` (${modRequests.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="topups" data-testid="tab-topups" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              Top-ups{topupRequests.length > 0 ? ` (${topupRequests.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="bins" data-testid="tab-bins" className="rounded-full data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black px-5">
              BINs{bins.length > 0 ? ` (${bins.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cards" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Card</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Nickname</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Owner</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Signature (public 16)</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Balance</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-cards-table">
                  {cards.map((c) => (
                    <TableRow key={c.id} className="border-white/5 hover:bg-white/[0.03]">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-md" style={{ background: THEME_DOT[c.color_theme] || THEME_DOT.aurora }} />
                          <span data-testid={`issued-card-number-${c.id}`} className="font-mono-sig text-xs text-[#A0A0AB]">{(c.card_number || "").replace(/(.{4})/g, "$1 ").trim()}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono-sig text-xs text-[#00F0FF]">{c.nickname || "—"}</TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{c.owner_name}</div>
                        <div className="text-[#52525B] text-xs">{c.owner_email}</div>
                      </TableCell>
                      <TableCell className="font-mono-sig text-xs text-[#00F0FF]">{c.signature_public}</TableCell>
                      <TableCell className="font-mono-sig text-sm">${c.balance.toFixed(2)}</TableCell>
                      <TableCell>
                        {c.frozen ? (
                          <Badge className="bg-[#00C2FF]/15 text-[#00C2FF] border-0">Frozen</Badge>
                        ) : (
                          <Badge className="bg-[#00FF9D]/15 text-[#00FF9D] border-0">Active</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {cards.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#52525B] py-10">No cards issued yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="registrar" className="mt-6 space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="px-5 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
                <Repeat className="w-4 h-4 text-[#FFE600] shrink-0" />
                <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">Card Registry · known &amp; transferable</span>
                <div className="relative ml-auto w-full sm:w-72">
                  <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="registrar-search"
                    value={regQuery}
                    onChange={(e) => setRegQuery(e.target.value)}
                    placeholder="Search card number, nickname, owner…"
                    className="w-full h-10 rounded-full bg-[#1A1A20] border border-white/10 text-white pl-9 pr-3 text-sm focus:outline-none focus:border-[#00F0FF]/50"
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Nickname</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Card number</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Owner</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Weight</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Uses</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Nonce</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Presence</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="registrar-table">
                  {registrar.cards
                    .filter((c) => {
                      const q = regQuery.trim().toLowerCase();
                      if (!q) return true;
                      const qDigits = q.replace(/\D/g, "");
                      return (
                        (c.nickname || "").toLowerCase().includes(q) ||
                        (c.owner_name || "").toLowerCase().includes(q) ||
                        (c.owner_email || "").toLowerCase().includes(q) ||
                        (qDigits && (c.card_number || "").includes(qDigits))
                      );
                    })
                    .map((c) => (
                    <TableRow key={c.id} className="border-white/5 hover:bg-white/[0.03]">
                      <TableCell className="font-mono-sig text-xs text-[#00F0FF]">{c.nickname || "—"}</TableCell>
                      <TableCell className="font-mono-sig text-xs text-white whitespace-nowrap">
                        {c.card_number ? c.card_number.replace(/(.{4})/g, "$1 ").trim() : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{c.owner_name}</div>
                        <div className="text-[#52525B] text-xs">{c.owner_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE[c.status] || STATUS_BADGE.active}>{c.status || "active"}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono-sig text-sm" style={{ color: WEIGHT_COLOR[c.weight?.class] || "#52525B" }}>
                          {c.weight?.score ?? 0} · {c.weight?.class ?? "—"}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono-sig text-sm text-[#FFE600]">{c.total_uses}</TableCell>
                      <TableCell className="font-mono-sig text-sm text-[#A0A0AB]">{c.nonce}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${c.online ? "bg-[#00FF9D] animate-pulse" : "bg-[#52525B]"}`} />
                          <span className="font-mono-sig text-[10px] uppercase tracking-wider text-[#A0A0AB]">{c.online ? `Online · ${c.online_count}` : "Offline"}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            data-testid={`registrar-freeze-${c.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => freezeCard(c)}
                            className={`rounded-full h-8 ${c.frozen ? "border-[#00C2FF]/50 bg-[#00C2FF]/15 text-[#00C2FF] hover:bg-[#00C2FF]/25" : "border-white/10 bg-white/5 text-white hover:bg-white/10"}`}
                          >
                            <Snowflake className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            data-testid={`registrar-lock-${c.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => toggleLock(c)}
                            className={`rounded-full h-8 ${c.locked ? "border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20" : "border-white/10 bg-white/5 text-white hover:bg-white/10"}`}
                          >
                            {c.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            data-testid={`registrar-transfer-${c.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setTransferCard(c)}
                            className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10 h-8"
                          >
                            <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Transfer
                          </Button>
                          {c.status !== "released" && (
                            <Button
                              data-testid={`registrar-remove-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => removeCard(c)}
                              className="rounded-full border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 h-8"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {registrar.cards.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-[#52525B] py-10">Registry is empty</TableCell>
                    </TableRow>
                  )}
                  {registrar.cards.length > 0 &&
                    registrar.cards.filter((c) => {
                      const q = regQuery.trim().toLowerCase();
                      if (!q) return true;
                      const qDigits = q.replace(/\D/g, "");
                      return (
                        (c.nickname || "").toLowerCase().includes(q) ||
                        (c.owner_name || "").toLowerCase().includes(q) ||
                        (c.owner_email || "").toLowerCase().includes(q) ||
                        (qDigits && (c.card_number || "").includes(qDigits))
                      );
                    }).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-[#52525B] py-10" data-testid="registrar-no-results">
                        No card matches “{regQuery}”
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4 text-[#7A5CFF]" />
                <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">Transfer Log</span>
              </div>
              {registrar.transfers.length === 0 ? (
                <p className="text-center text-[#52525B] py-8 text-sm">No transfers yet</p>
              ) : (
                <div className="divide-y divide-white/5" data-testid="transfer-log">
                  {registrar.transfers.map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-5 py-3 text-sm">
                      <span className="font-mono-sig text-[#00F0FF]">{t.nickname}</span>
                      <span className="text-[#A0A0AB] truncate mx-3">{t.from_email} → {t.to_email}</span>
                      <span className="font-mono-sig text-[10px] text-[#52525B] shrink-0">{new Date(t.at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="requests-list">
              {requests.length === 0 ? (
                <p className="text-center text-[#52525B] py-12 text-sm">No pending card requests</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {requests.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-4 px-5 py-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-medium text-white">{q.user_name} <span className="text-[#52525B] text-sm">· {q.user_email}</span></p>
                        <p className="text-sm text-[#A0A0AB]">
                          {q.nickname ? <span className="font-mono-sig text-[#00F0FF]">{q.nickname}</span> : <span className="text-[#52525B]">no nickname</span>}
                          {q.amount ? <span className="font-mono-sig text-[#FFE600]"> · ${Number(q.amount).toFixed(2)}</span> : ""}
                          {q.note ? ` — ${q.note}` : ""}
                        </p>
                        <p className="font-mono-sig text-[10px] text-[#52525B] mt-1">{new Date(q.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`issue-request-${q.id}`}
                          size="sm"
                          onClick={() => openIssueForRequest(q)}
                          className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 h-9 font-semibold"
                        >
                          <Plus className="w-4 h-4 mr-1.5" /> Issue
                        </Button>
                        <Button
                          data-testid={`decline-request-${q.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => declineRequest(q)}
                          className="rounded-full border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 h-9"
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Name</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Email</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Identity</TableHead>
                    {isAdmin && <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider text-right">Moderator</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-users-table">
                  {users.map((u) => (
                    <TableRow key={u.user_id} className="border-white/5 hover:bg-white/[0.03]">
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-[#A0A0AB]">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={u.role === "admin" ? "bg-[#FF007F]/15 text-[#FF007F] border-0" : u.role === "moderator" ? "bg-[#7A5CFF]/15 text-[#7A5CFF] border-0" : "bg-white/10 text-[#A0A0AB] border-0"}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.has_id_document ? (
                          <Button
                            data-testid={`view-id-${u.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`${BACKEND_URL}/api/admin/users/${u.user_id}/id-document`, "_blank")}
                            className="rounded-full h-8 border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20"
                          >
                            <IdCard className="w-3.5 h-3.5 mr-1.5" /> View ID
                          </Button>
                        ) : (
                          <span className="font-mono-sig text-[10px] text-[#52525B] uppercase">{u.auth_provider === "email" ? "no doc" : "google"}</span>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          {u.role !== "admin" && (
                            <Button
                              data-testid={`mod-toggle-${u.user_id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => toggleModerator(u)}
                              className={`rounded-full h-8 ${u.role === "moderator" ? "border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20" : "border-[#7A5CFF]/40 bg-[#7A5CFF]/10 text-[#7A5CFF] hover:bg-[#7A5CFF]/20"}`}
                            >
                              {u.role === "moderator" ? "Remove" : "Make moderator"}
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="mods" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="mod-requests-list">
              {modRequests.length === 0 ? (
                <p className="text-center text-[#52525B] py-12 text-sm">No pending moderator requests</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {modRequests.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-4 px-5 py-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-medium text-white">{q.name} <span className="text-[#52525B] text-sm">· {q.email}</span></p>
                        {q.note ? <p className="text-sm text-[#A0A0AB]">{q.note}</p> : null}
                        <p className="font-mono-sig text-[10px] text-[#52525B] mt-1">{new Date(q.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`approve-mod-${q.id}`}
                          size="sm"
                          onClick={() => approveMod(q)}
                          className="rounded-full bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 h-9 font-semibold"
                        >
                          Approve
                        </Button>
                        <Button
                          data-testid={`decline-mod-${q.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => declineMod(q)}
                          className="rounded-full border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 h-9"
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="topups" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="topup-requests-list">
              {topupRequests.length === 0 ? (
                <p className="text-center text-[#52525B] py-12 text-sm">No pending top-up requests</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {topupRequests.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-4 px-5 py-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-medium text-white">
                          <span className="font-mono-sig text-[#00FF9D]">${Number(q.amount).toFixed(2)}</span> · {q.nickname}
                          <span className="text-[#52525B] text-sm"> · {q.cardholder_name}</span>
                        </p>
                        <p className="text-sm text-[#A0A0AB]">{q.email}</p>
                        <p className="font-mono-sig text-[10px] text-[#52525B] mt-1">{new Date(q.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`approve-topup-${q.id}`}
                          size="sm"
                          onClick={() => approveTopup(q)}
                          className="rounded-full bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 h-9 font-semibold"
                        >
                          Approve
                        </Button>
                        <Button
                          data-testid={`decline-topup-${q.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => declineTopup(q)}
                          className="rounded-full border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 h-9"
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="bins" className="mt-6 space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="bin-create-panel">
              <p className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB] mb-3">Register an issuer BIN</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-[10px] text-[#52525B] uppercase tracking-wider mb-1.5">BIN (4–12 digits)</label>
                  <input
                    data-testid="bin-input"
                    value={newBin}
                    onChange={(e) => setNewBin(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="506099"
                    className="w-full h-11 rounded-xl bg-[#1A1A20] border border-white/10 text-white px-3 font-mono-sig"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-[10px] text-[#52525B] uppercase tracking-wider mb-1.5">Label (optional)</label>
                  <input
                    data-testid="bin-label-input"
                    value={newBinLabel}
                    onChange={(e) => setNewBinLabel(e.target.value)}
                    placeholder="SIGNET Prime"
                    className="w-full h-11 rounded-xl bg-[#1A1A20] border border-white/10 text-white px-3"
                  />
                </div>
                <Button
                  data-testid="bin-create-button"
                  onClick={createBin}
                  className="rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-11"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Register BIN
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">BIN</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Label</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Cards issued</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-[#52525B] font-mono-sig text-[10px] uppercase tracking-wider text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="bins-table">
                  {bins.map((b) => (
                    <TableRow key={b.id} className="border-white/5 hover:bg-white/[0.03]">
                      <TableCell className="font-mono-sig text-sm text-[#00F0FF]">{b.bin}</TableCell>
                      <TableCell className="text-sm">{b.label || "—"}</TableCell>
                      <TableCell className="font-mono-sig text-sm text-[#FFE600]">{b.cards_issued}</TableCell>
                      <TableCell>
                        <Badge className={b.active ? "bg-[#00FF9D]/15 text-[#00FF9D] border-0" : "bg-white/10 text-[#A0A0AB] border-0"}>
                          {b.active ? "active" : "inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          data-testid={`bin-toggle-${b.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => toggleBin(b)}
                          className="rounded-full h-8 border-white/10 bg-white/5 text-white hover:bg-white/10"
                        >
                          {b.active ? "Deactivate" : "Activate"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {bins.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-[#52525B] py-10">No BINs registered yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <IssueCardDialog
        open={issueOpen}
        onOpenChange={(o) => { setIssueOpen(o); if (!o) setIssuePrefill(null); }}
        users={users}
        prefill={issuePrefill}
        onCreated={loadAll}
      />
      {transferCard && (
        <TransferDialog
          open={!!transferCard}
          onOpenChange={(o) => !o && setTransferCard(null)}
          card={transferCard}
          isAdmin
          onTransferred={() => { setTransferCard(null); loadAll(); }}
        />
      )}
    </div>
  );
}
